import {
  Component,
  inject,
  OnInit,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';
import {
  ConditionMatchType,
  Menu,
  MenuCondition,
  MenuList,
  MenuType,
} from '../../models/menu';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { MenuService } from '../../services/menu/menu.service';
import { AsyncPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../../pages/auth/services/auth.service';
import { CRMService } from '../../services/CRM/crm.service';
import { ImageService } from '../../services/image-service/image.service';
import { takeUntil } from 'rxjs';
import { DestroyBase } from '../../base-classes/destroy-component-base';
import { environment } from '../../../../environments/environment';
import { TourService } from '../../services/tour-service/tour.service';
import { CommonService } from '../../services/common/common.service';

@Component({
  selector: 'app-left-menu',
  standalone: true,
  imports: [SvgIconComponent, AsyncPipe, RouterModule],
  templateUrl: './left-menu.component.html',
  styleUrl: './left-menu.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class LeftMenuComponent extends DestroyBase implements OnInit {
  menuService = inject(MenuService);
  authService = inject(AuthService);
  crmService = inject(CRMService);
  renderer = inject(Renderer2);
  imageService = inject(ImageService);
  tourService = inject(TourService);
  commonService = inject(CommonService);

  readonly isSMEUser = this.authService.isSMEUser();

  mainMenu = MenuList.filter((i) => {
    const matchesConditions = this.matchesMenuConditions(
      i.conditions,
      i.conditionMatchType,
    );

    // First check if the main menu item has permission
    if (
      i.type === MenuType.Main &&
      this.authService.hasPermission(i.permission) &&
      matchesConditions
    ) {
      // smeOnly items are only visible to SME users
      if (i.smeOnly && !this.authService.isSMEUser()) {
        return false;
      }

      // If it has sub-items, filter those based on permissions
      if (i.sub.length > 0) {
        i.sub = i.sub.filter((subItem) => {
          return (
            this.authService.hasPermission(subItem.permission) &&
            this.matchesMenuConditions(
              subItem.conditions,
              subItem.conditionMatchType,
            )
          );
        });
      }
      return true;
    }
    return false;
  });

  adminMenu = MenuList.filter((i) => {
    // Same logic for admin menu
    if (
      i.type === MenuType.Admin &&
      this.authService.hasPermission(i.permission)
    ) {
      if (i.sub.length > 0) {
        i.sub = i.sub.filter((subItem) =>
          this.authService.hasPermission(subItem.permission),
        );
      }
      return true;
    }
    return false;
  });

  selectedMenuItem$ = this.menuService.menu$;
  isMenuCollapsed = false;
  connectionUrl: string | undefined = undefined;
  connectionName: string | undefined = undefined;
  imageLoaded = true;
  crmStatus = false;
  showCRMIntegration = environment.showCRMIntegration;

  currentYear = new Date().getFullYear();

  matchesMenuConditions(
    conditions: MenuCondition[] = [],
    matchType: ConditionMatchType = ConditionMatchType.All,
  ): boolean {
    if (!conditions.length) {
      return true;
    }

    const context = {
      authService: this.authService,
    };

    const results = conditions.map((condition) => condition.check(context));

    return matchType === ConditionMatchType.All
      ? results.every(Boolean)
      : results.some(Boolean);
  }

  ngOnInit(): void {
    const loggedInUser = this.authService.getLoggedInUserInfo();

    this.connectionName =
      loggedInUser?.connection_display_name || loggedInUser?.connection || '';

    if (this.connectionName) {
      this.commonService.updateConnectionName(this.connectionName);
    }

    this.commonService.connectionName$
      .pipe(takeUntil(this.destroy$))
      .subscribe((name) => {
        if (name) {
          this.connectionName = name;
        }
      });

    // Subscribe to connection image
    this.imageService.connectionImageUrl$
      .pipe(takeUntil(this.destroy$))
      .subscribe((url) => {
        if (url) {
          this.connectionUrl = url;
          this.imageLoaded = true;
        } else {
          this.connectionUrl = undefined;
          this.imageLoaded = false;
        }
      });

    if (this.showCRMIntegration) {
      this.loadCRMStatus();
    }
  }

  loadCRMStatus(): void {
    localStorage.setItem('crm_status', 'false');
    this.authService
      .getCRMAuthToken()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (crmToken) => {
          if (crmToken) {
            localStorage.setItem('crm_token', crmToken.access!);
            localStorage.setItem(
              'crm_token_expires_on_utc',
              crmToken.token_expiry!,
            );
            localStorage.setItem('crm_status', 'true'); // If response comes, CRM is enabled
            this.crmStatus = true;
          }
        },
        error: (error) => {
          // Handle 403 (CRM disabled) and other errors gracefully
          console.warn('CRM is disabled or unavailable:', error);
          localStorage.setItem('crm_status', 'false');
          this.crmStatus = false;
        },
      });
  }

  onImageError(): void {
    this.imageLoaded = false;
  }

  openSubmenu(menuItem: Menu) {
    menuItem.isExpanded = true;
  }

  toggleMenu(): void {
    this.isMenuCollapsed = !this.isMenuCollapsed;
    if (this.isMenuCollapsed) {
      this.renderer.addClass(document.body, 'menu-collapsed');
    } else {
      this.renderer.removeClass(document.body, 'menu-collapsed');
    }
  }

  toggleSubmenu(menuItem: Menu): void {
    menuItem.isExpanded = !menuItem.isExpanded;
  }

  isSubMenuActive(menuItem: Menu, selectedItem: Menu): boolean {
    return menuItem.sub.some((subItem) => subItem === selectedItem);
  }

  openCRM(): void {
    const crmToken = localStorage.getItem('crm_token');
    const crmTokenExpiresOn = localStorage.getItem('crm_token_expires_on_utc');

    const isTokenValid =
      crmToken && crmTokenExpiresOn && new Date(crmTokenExpiresOn) > new Date();

    if (isTokenValid) {
      this.navigateToCRM(crmToken);
    } else {
      this.authService
        .getCRMAuthToken()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (crmRecord) => {
            localStorage.setItem('crm_token', crmRecord.access!);
            localStorage.setItem(
              'crm_token_expires_on_utc',
              crmRecord.token_expiry!,
            );
            this.navigateToCRM(crmRecord.access!);
          },
          error: (error) => {
            console.warn(
              'Failed to get CRM token (CRM may be disabled):',
              error,
            );
            // Don't navigate to CRM if token fetch fails
          },
        });
    }
  }

  private navigateToCRM(token: string): void {
    const crmUrl = `${environment.crmUrl}/index.php?module=Leads&view=List&viewname=1&app=MARKETING&token=${token}`;
    window.open(crmUrl, '_blank');
  }
}
